import React from 'react'
import styled from 'styled-components/macro'
import { margin } from 'styled-system'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { themeGet } from '@styled-system/theme-get'
import { ErrorSVG } from '../../../../components/Icons/Basic'
import {
  Tooltip,
  TooltipPositions,
  TooltipOverlayType,
} from '../../../../components/Reusable/Tooltip/Tooltip'
import { InfoButton } from '../../../../components/PageLayout/InfoButton'
import palette from '../../../../designSystem/variables/palette'

export const MissingItemIcon = styled(ErrorSVG).attrs({
  fill: 'fill.status.error.dark',
  mr: 'xxxs',
})`
  ${margin}
  width: 20px;
  height: 20px;
`

export const RelatedPolicy = styled.div`
  a {
    text-decoration: none;
  }
  svg {
    margin-left: 5px;
    fill: ${themeGet('colors.text.icon.default')};
  }
`

export const HowToHandleItHeader = styled.h1`
  display: inline-flex;
  align-items: center;
  margin: 0;
  font-weight: ${themeGet('fontWeights.semiBold')};
  font-size: 14px;
  line-height: 21px;
`

export const ControlDescriptionContainer = styled.main.attrs((props) => {
  return {
    mt: 'l',
    children: (
      <>
        <h1>
          Procedure
          <Tooltip
            id="showpage-item-description-tooltip"
            placement={TooltipPositions.right}
            overlayType={TooltipOverlayType.popover}
            tooltipBody="Please write a few sentences describing how you achieve the control objective. This should cover the basic who, what, when, where, how of the control."
          >
            <InfoButton as="span" ml="xxxs" />
          </Tooltip>
        </h1>
        {props.children}
      </>
    ),
  }
})`
  ${margin}
  h1 {
    display: inline-flex;
    align-items: center;
    margin: 0;
    font-weight: ${themeGet('fontWeights.semiBold')};
    font-size: 14px;
    line-height: 21px;
  }
`
export const ControlNoteContainer = styled.main.attrs((props) => {
  return {
    mt: 'l',
    children: (
      <>
        <h1>Notes</h1>
        {props.children}
      </>
    ),
  }
})`
  ${margin}
  h1 {
    display: inline-flex;
    align-items: center;
    margin: 0;
    font-weight: ${themeGet('fontWeights.semiBold')};
    font-size: 14px;
    line-height: 21px;
  }
`
export const DateFilterWrapper = styled.div`
  grid-column: 9 / -1;
  position: relative;
  width: 100%;
`

export const DateFilterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  z-index: 1;
  top: 0;
  right: 0;
  width: 100%;
  height: 39px;
  font-size: 16px;
  font-weight: 400;
  color: ${palette.neutral[900]};

  > *:last-child {
    margin: 0 12px;
  }
`

export const NotApplicableSection = styled.article.attrs({
  mb: 'm',
})`
  ${margin}
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export const NotApplicableObjective = styled.div`
  font-size: 18px;
  color: ${palette.neutral[400]};
`

export const NotApplicableNotesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 24px;
  border-radius: 14px;
  border: 1px solid ${palette.neutral[100]};
`

export const NotApplicableNotesHeader = styled.div`
  display: flex;
`

export const NotApplicableNotesIcon = styled(FontAwesomeIcon)`
  font-size: 18px;
  color: ${palette.neutral[600]};
  font-weight: 900;
`

export const NotApplicableNotesDescription = styled.div`
  margin: 0 auto;
  color: ${palette.neutral[600]};
  font-weight: 300;
`
export const ControlOption = styled.div<{ background: string }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px;
  border-radius: 8px;
  width: 100%;
  cursor: pointer;
  background: ${({ background }) => background};
  transition: background 0.15s ease-in-out;
  font-weight: 400;
  font-size: 14px;
  line-height: 150%;

  &:hover {
    filter: brightness(0.9);
  }
`

export const PolicyLinkIcon = styled(FontAwesomeIcon)`
  font-size: 18px;
  color: ${palette.neutral[600]};
  font-weight: 900;
`

export const ControlCardsGrid = styled.div<{
  $numCards: number
  $gridTemplateColumns?: string
}>`
  display: grid;
  grid-template-columns: ${({ $numCards, $gridTemplateColumns }) =>
    $gridTemplateColumns || `repeat(${$numCards}, 1fr)`};
  gap: 12px;
`

export const ControlCardButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.15s ease-in-out;
  svg {
    width: 16px;
    height: 16px;
  }
`

export const ControlCardContainer = styled.div<{
  $fixedHeight?: number
  $minHeight?: number
  $maxHeight?: number
  $isEditing?: boolean
}>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-radius: 20px;
  border: 1px solid ${palette.neutral[100]};
  padding: 12px;
  ${({ $fixedHeight, $minHeight, $maxHeight, $isEditing }) =>
    !$isEditing &&
    ($minHeight || $maxHeight
      ? `
    ${$minHeight ? `min-height: ${$minHeight}px;` : ''}
    ${$maxHeight ? `max-height: ${$maxHeight}px;` : ''}
    height: 100%;
  `
      : $fixedHeight
      ? `
    height: ${$fixedHeight}px;
    overflow: hidden;
  `
      : '')}
  ${({ $isEditing }) =>
    $isEditing &&
    `
    height: 100%;
  `}
  &:hover ${ControlCardButtonGroup} {
    opacity: 1;
  }

  &:has(${ControlCardButtonGroup}):hover {
    background-color: ${palette.neutral[30]};
  }
`

export const ControlCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  flex-shrink: 0;
`

export const ControlCardTitle = styled.div<{
  $preserveIconColor?: boolean
}>`
  font-size: 20px;
  font-weight: 400;
  line-height: normal;
  color: ${palette.neutral[900]};
  display: flex;
  align-items: center;
  gap: 8px;
  .control-card-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    path {
      fill: ${({ $preserveIconColor }) =>
        !$preserveIconColor && palette.neutral[900]};
    }
  }
`

export const ControlCardBody = styled.div<{
  $isEditing?: boolean
  $hasFixedHeight?: boolean
}>`
  font-size: 14px;
  font-weight: 400;
  line-height: normal;
  color: ${palette.neutral[900]};
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  ${({ $isEditing }) =>
    $isEditing &&
    `
    height: 100%;
  `}
`
